import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Sparkles, Clock, Gift, CreditCard, FileText, ArrowRight, Brain } from "lucide-react";
import { Link } from "react-router-dom";
import { useN2Offer } from "../lib/useN2Offer";
import { formatCOP } from "../lib/apiClient";

/**
 * FlowGuide — Tarjeta de auto-orientación reutilizable para cada flujo (N0/N1/N2).
 *
 * Iter25 — Self-work UX: cuando el usuario abre cualquier paso (Prevalidación,
 * Diagnóstico, Kit), un panel colapsable muestra de un vistazo:
 *   • Qué es este paso       • Qué te entrega
 *   • Cuánto tarda            • Cuánto cuesta (con cuota / sin cuota)
 *   • Pain-point específico (cómo pasar al siguiente nivel; si tengo bono cómo lo uso)
 *   • Transparencia IA: dónde investigó, qué encontró, por qué cada paso
 *
 * Props:
 *  - level: "n0" | "n1" | "n2"
 *  - quota: objeto user_quotas { diagnoses_remaining, kits_remaining, ... } (opcional)
 *  - state: estado actual del caso para "qué sigue" contextual
 *  - caseId: para "Ver guía paso anterior/siguiente"
 *  - storageKey: clave localStorage para persistir colapsado por flujo (default: per-level)
 *
 * Diseño: colapsable por defecto-abierto en primera visita. Botón "?" persistente
 * en la esquina re-abre la guía.
 */

const LEVELS_META = {
  n0: {
    title: "Prevalidación",
    short: "N0",
    color: "bg-sky-50 border-sky-200 text-sky-900",
    iconColor: "text-sky-600",
    icon: <Sparkles className="w-4 h-4" />,
    description:
      "Una primera lectura técnica de tu necesidad de contratación. La IA revisa tu caso y clasifica el estado de la información: Lista para diagnóstico / Información pendiente / Requiere revisión, con explicación corta.",
    delivers: [
      "Clasificación de información (Lista para diagnóstico / Información pendiente / Requiere revisión)",
      "Justificación corta (3–5 líneas)",
      "Recomendación de siguiente paso",
    ],
    duration: "1 a 3 minutos",
    cost: "Gratis",
    paidNote: null,
    aiTransparency: [
      "La IA lee solo lo que tú escribiste en el formulario.",
      "No consulta SECOP ni normativa en este paso (eso ocurre en Diagnóstico).",
      "Compara contra patrones de inviabilidad jurídica frecuentes (objeto vago, fragmentación, etc).",
    ],
    howToProceed:
      "Cuando el estado sea 'Lista para diagnóstico' verás el botón verde 'Continuar a Diagnóstico'. Si marca 'Información pendiente' o 'Requiere revisión', completa o corrige los datos antes de continuar.",
  },
  n1: {
    title: "Diagnóstico",
    short: "N1",
    color: "bg-amber-50 border-amber-200 text-amber-900",
    iconColor: "text-amber-600",
    icon: <Brain className="w-4 h-4" />,
    description:
      "Análisis precontractual completo con comparables reales de SECOP y normativa colombiana aplicable. Te entrega un dictamen revisado por humano experto y un plan de ruta a seguir.",
    delivers: [
      "Análisis del mercado (≥3 contratos comparables SECOP)",
      "Identificación de modalidad (Mínima Cuantía, Selección Abreviada, etc.)",
      "Riesgos jurídicos identificados",
      "Plan de ruta recomendado",
      "Revisión humana experta antes de entrega",
    ],
    duration: "24 a 48 horas (incluye revisión humana)",
    cost: "$350.000 COP / si tienes cuota gratis aplica 1 cuota",
    paidNote:
      "Si tienes cuota gratuita disponible, el botón dirá 'Usar cuota gratuita' (no necesitas pagar). Si no tienes cuota, te llevará a pagar por MercadoPago, transferencia o Bre-B.",
    aiTransparency: [
      "La IA busca en SECOP I y SECOP II contratos similares (objeto + monto + región).",
      "Filtra mínimo 3 contratos comparables del último año.",
      "Cruza con la base normativa interna (Ley 80, Decreto 1082, etc.).",
      "Un revisor humano experto valida la salida antes de entregártela.",
    ],
    howToProceed:
      "Cuando el Diagnóstico esté entregado, verás un botón 'Generar Kit Documental' (Nivel 2) para descargar el paquete editable de documentos.",
  },
  n2: {
    title: "Kit Documental",
    short: "N2",
    color: "bg-emerald-50 border-emerald-200 text-emerald-900",
    iconColor: "text-emerald-600",
    icon: <FileText className="w-4 h-4" />,
    description:
      "Paquete editable de documentos precontractuales listos para personalizar y firmar. Incluye Estudios Previos, Análisis del Sector, Matriz de Riesgos, Especificaciones, Invitación y Checklist legal.",
    delivers: [
      "Estudios Previos (Word editable)",
      "Análisis del Sector con datos SECOP",
      "Matriz de Riesgos completa",
      "Especificaciones Técnicas base",
      "Invitación a presentar ofertas",
      "Checklist legal por modalidad",
      "Empaque ZIP descargable + revisión humana",
    ],
    duration: "48 a 72 horas (incluye revisión humana)",
    cost: "N2_COST",
    paidNote:
      "Igual que en Diagnóstico: si tienes cuota gratuita disponible para Kit Documental, el botón dirá 'Usar cuota gratuita' y NO pasarás por el pago.",
    aiTransparency: [
      "Usa el contexto completo: tu intake N0, el Diagnóstico aprobado, comparables SECOP y tu información N2.",
      "Genera cada documento aplicando jurisprudencia y normativa filtrada.",
      "El revisor humano valida 10 ítems del checklist legal antes de aprobar.",
      "Recibirás el ZIP con todos los .docx editables.",
    ],
    howToProceed:
      "Cuando el Kit esté aprobado, recibirás un correo con el link de descarga del ZIP. También aparecerá un botón 'Descargar Kit' en tu dashboard.",
  },
};

export default function FlowGuide({ level, quota, state, caseId, storageKey }) {
  const meta = LEVELS_META[level];
  const n2Offer = useN2Offer();
  const key = storageKey || `flowguide_${level}_collapsed`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? true : v === "open";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, open ? "open" : "collapsed");
    } catch { /* localStorage no disponible */ }
  }, [key, open]);

  if (!meta) return null;

  // Detectar si user tiene cuota relevante para mostrar mensaje específico
  const hasN1Quota = level === "n1" && quota && (quota.diagnoses_remaining ?? 0) > 0;
  const hasN2Quota = level === "n2" && quota && (quota.kits_remaining ?? 0) > 0;
  const hasQuota = hasN1Quota || hasN2Quota;
  const remaining = hasN1Quota ? quota.diagnoses_remaining : (hasN2Quota ? quota.kits_remaining : 0);

  return (
    <div
      className={`${meta.color} border rounded-sm overflow-hidden transition-all`}
      data-testid={`flow-guide-${level}`}
    >
      {/* Header colapsable */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-black/5 transition-colors"
        data-testid={`flow-guide-${level}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div className={`${meta.iconColor}`}>{meta.icon}</div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest opacity-70">Guía del paso</p>
            <p className="text-sm font-semibold">{meta.title} <span className="opacity-60 font-normal">— ¿qué es y qué entrega?</span></p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
      </button>

      {/* Cuerpo */}
      {open && (
        <div className="px-4 pb-4 space-y-4 text-sm">
          {/* Cuota highlight si aplica */}
          {hasQuota && (
            <div className="bg-emerald-100/70 border border-emerald-300 px-3 py-2 rounded-sm flex items-start gap-2" data-testid={`flow-guide-${level}-quota-banner`}>
              <Gift className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
              <div className="text-emerald-900 text-xs leading-relaxed">
                <strong>¡Tienes {remaining} cuota{remaining !== 1 ? "s" : ""} gratuita{remaining !== 1 ? "s" : ""} disponible{remaining !== 1 ? "s" : ""}!</strong> No necesitas pagar:
                cuando hagas click en &quot;Continuar&quot;, verás el botón <strong>&quot;Usar cuota gratuita&quot;</strong> (verde) en la parte superior
                del paso siguiente, y NO pasarás por la pasarela de pago.
              </div>
            </div>
          )}

          {/* Descripción */}
          <div>
            <p className="text-xs uppercase tracking-widest opacity-70 mb-1">¿Qué es?</p>
            <p className="text-sm leading-relaxed opacity-90">{meta.description}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="bg-white/60 border border-white rounded-sm p-2">
              <p className="opacity-70 uppercase tracking-widest text-[9px] mb-0.5">Tiempo estimado</p>
              <p className="font-semibold flex items-center gap-1.5"><Clock className="w-3 h-3" />{meta.duration}</p>
            </div>
            <div className="bg-white/60 border border-white rounded-sm p-2">
              <p className="opacity-70 uppercase tracking-widest text-[9px] mb-0.5">Costo</p>
              <p className="font-semibold flex items-center gap-1.5"><CreditCard className="w-3 h-3" />{meta.cost === "N2_COST" ? `${formatCOP(n2Offer.price_reviewed_cop)} COP (con revisión) / si tienes cuota gratis aplica 1 cuota` : meta.cost}</p>
            </div>
            <div className="bg-white/60 border border-white rounded-sm p-2">
              <p className="opacity-70 uppercase tracking-widest text-[9px] mb-0.5">Nivel</p>
              <p className="font-semibold">{meta.short} — {meta.title}</p>
            </div>
          </div>

          {/* Qué entrega */}
          <div>
            <p className="text-xs uppercase tracking-widest opacity-70 mb-1.5">¿Qué te entrega?</p>
            <ul className="space-y-1">
              {meta.delivers.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-xs opacity-90">
                  <span className={`${meta.iconColor} mt-0.5`}>✓</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Transparencia IA (pain point d) */}
          <details className="bg-white/40 border border-white/80 rounded-sm" data-testid={`flow-guide-${level}-ai-details`}>
            <summary className="px-3 py-2 cursor-pointer text-xs font-medium flex items-center gap-2 hover:bg-white/60">
              <Brain className="w-3.5 h-3.5" />
              ¿Cómo funciona la IA en este paso? <span className="opacity-60 ml-1">(click para ver)</span>
            </summary>
            <div className="px-3 pb-3 pt-1">
              <ul className="space-y-1.5 text-xs opacity-90">
                {meta.aiTransparency.map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="opacity-60">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </details>

          {/* Cómo pasar al siguiente (pain point a) */}
          <div className="bg-white/60 border-l-4 border-l-current pl-3 pr-2 py-2 rounded-r-sm">
            <p className="text-[10px] uppercase tracking-widest opacity-70 mb-1 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" />¿Cómo paso al siguiente paso?
            </p>
            <p className="text-xs leading-relaxed">{meta.howToProceed}</p>
          </div>

          {/* Pago vs cuota explicado (pain point b) */}
          {meta.paidNote && (
            <div className="bg-white/40 border border-white rounded-sm p-2 text-[11px] leading-relaxed opacity-90">
              <strong className="block mb-0.5">💡 Cuotas vs pago:</strong>
              {meta.paidNote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * FlowGuideTrigger — Botón flotante "?" persistente para reabrir la guía.
 * Resetea la persistencia local del FlowGuide y dispara `onOpen`.
 */
export function FlowGuideTrigger({ level, onOpen }) {
  const reopen = () => {
    try {
      localStorage.setItem(`flowguide_${level}_collapsed`, "open");
    } catch { /* noop */ }
    onOpen?.();
  };
  return (
    <button
      type="button"
      onClick={reopen}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-sm hover:border-brand-burgundy hover:text-brand-burgundy transition-colors"
      data-testid={`flow-guide-${level}-trigger`}
      title={`Ver guía del paso ${level.toUpperCase()}`}
    >
      <HelpCircle className="w-3.5 h-3.5" />
      <span>Ver guía</span>
    </button>
  );
}
